import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Constants
import GPT3Topics from './GPT3Topics.constants';

// Types
import { FeedbackRecognitionTemplateTypes } from 'Shared/constants/types';

// Components
import WpAlert from 'Wpds/components/alert/WpAlert';
import FeedbackFormSection from '../FeedbackFormSection/FeedbackFormSection';
import FeedbackFormFieldContainer from '../FeedbackFormFieldContainer/FeedbackFormFieldContainer';
import GPT3StepSelections from './GPT3StepSelections';
import GPT3StepChoose from './GPT3StepChoose';

// Style
import './FeedbackGPT3coach.scss';

const FeedbackGPT3coach = () => {
  const STEPS = { selections: 'selections', choose: 'choose', edit: 'edit' };

  const steps = [STEPS.selections, STEPS.choose, STEPS.edit];
  const [currentStep, setCurrentStep] = useState(STEPS.selections);
  const [selectedStrengths, setSelectedStrengths] = useState([]);
  const [selectedImprovements, setSelectedImprovements] = useState([]);
  const [strengthExamples, setStrengthExamples] = useState('');
  const [improvementExamples, setImprovementExamples] = useState('');
  const [generatedOptions, setGeneratedOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [shouldFetchSuggestions, setShouldFetchSuggestions] = useState(false);
  const [customStrengthAttributes, setCustomStrengthAttributes] = useState([]);
  const [customImprovementAttributes, setCustomImprovementAttributes] = useState([]);

  const { pathname } = useLocation();
  const isEditFeedbackPage = pathname.includes('edit');
  const { allowOnlyStrengthOrOnlyWeaknessInFeedbackai } = useFlags();
  const showNextButton = !isEditFeedbackPage && currentStep !== STEPS.edit;
  const showBackButton = !isEditFeedbackPage && currentStep !== STEPS.selections;
  const addCustomAttributeEvent = 'Added Custom Attribute in Feedback AI Creation';

  useEffect(() => {
    if (isEditFeedbackPage) setCurrentStep(STEPS.edit);
  }, []);

  const handleContinueClick = () => {
    let hasError = false;
    if (
      currentStep === STEPS.selections &&
      !allowOnlyStrengthOrOnlyWeaknessInFeedbackai &&
      (selectedStrengths.length === 0 || selectedImprovements.length === 0)
    ) {
      setErrorMessage('Please select at least one strength and one improvement.');
      hasError = true;
    }

    if (
      currentStep === STEPS.selections &&
      allowOnlyStrengthOrOnlyWeaknessInFeedbackai &&
      selectedStrengths.length === 0 &&
      selectedImprovements.length === 0
    ) {
      setErrorMessage('Please select at least one strength or one improvement.');
      hasError = true;
    }

    if (currentStep === STEPS.choose && selectedOption === '') {
      setErrorMessage('Please select a suggestion.');
      hasError = true;
    }

    if (!hasError) {
      setErrorMessage('');
      setCurrentStep(steps[steps.indexOf(currentStep) + 1]);
    }
  };

  useEffect(() => {
    setShouldFetchSuggestions(true);
    setSelectedOption('');
  }, [selectedStrengths, selectedImprovements]);

  useEffect(() => {
    setShouldFetchSuggestions(false);
  }, [currentStep]);

  const handleBackClick = () => {
    setCurrentStep(steps[steps.indexOf(currentStep) - 1]);
  };

  const handeAddCustomStrengthAttribute = value => {
    setCustomStrengthAttributes([...customStrengthAttributes, value]);
    (window as any).analytics.track(addCustomAttributeEvent, { attributeType: 'strength', value });
  };

  const handeAddCustomImprovementAttribute = value => {
    setCustomImprovementAttributes([...customImprovementAttributes, value]);
    (window as any).analytics.track(addCustomAttributeEvent, { attributeType: 'weakness', value });
  };

  const strengthAttributes = [...GPT3Topics.strengths, ...customStrengthAttributes];
  const improvementAttributes = [...GPT3Topics.improvements, ...customImprovementAttributes];

  const baseClassName = 'FeedbackGPT3coach';

  return (
    <div className={baseClassName}>
      <FeedbackFormSection
        templateType={FeedbackRecognitionTemplateTypes.feedbackGPT3Coach}
        showRequestFeedback={false}
        allowSaveAsDraft={false}
        handleNext={showNextButton && handleContinueClick}
        handleBack={showBackButton && handleBackClick}
        values={{ likeThatText: selectedOption }}
        errorMessage={
          errorMessage && (
            <WpAlert type="danger" onDismiss={() => setErrorMessage('')}>
              {errorMessage}
            </WpAlert>
          )
        }
      >
        {currentStep === 'selections' && (
          <GPT3StepSelections
            selectedStrengths={selectedStrengths}
            selectedImprovements={selectedImprovements}
            strengthAttributeList={strengthAttributes}
            strengthImprovementList={improvementAttributes}
            onSelectStrengths={setSelectedStrengths}
            onSelectImprovements={setSelectedImprovements}
            handeAddCustomStrengthAttribute={handeAddCustomStrengthAttribute}
            handeAddCustomImprovementAttribute={handeAddCustomImprovementAttribute}
            strengthExamples={strengthExamples}
            setStrengthExamples={setStrengthExamples}
            improvementExamples={improvementExamples}
            setImprovementExamples={setImprovementExamples}
          />
        )}
        {currentStep === 'choose' && (
          <GPT3StepChoose
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            strengths={selectedStrengths}
            improvements={selectedImprovements}
            strengthExamples={strengthExamples}
            improvementExamples={improvementExamples}
            generatedOptions={generatedOptions}
            setGeneratedOptions={setGeneratedOptions}
            shouldFetch={shouldFetchSuggestions}
          />
        )}
        {currentStep === 'edit' && (
          <FeedbackFormFieldContainer
            title="Edit and send your feedback"
            textFieldName="likeThatText"
            templateType={FeedbackRecognitionTemplateTypes.feedbackGPT3Coach}
            rows={8}
          />
        )}
        <div className={`${baseClassName}__support-label`}>This tool is powered by GPT-3, from OpenAI.</div>
      </FeedbackFormSection>
    </div>
  );
};

export default FeedbackGPT3coach;
